import {yupResolver} from "@hookform/resolvers/yup";
import * as Yup from "yup";

const FIELD_NAME_RE = /^[a-z][a-z0-9_]*$/;

// Fields this module manages itself for its own workflow (state transitions, correlation, work
// notes) are excluded so a mapping can never clobber them. assignment_group can be mapped like
// any other field — the backend resolves a mapped group name the same way as the dedicated
// setting, so it's not excluded here.
const RESERVED_FIELD_NAMES = new Set([
  "sys_id",
  "state",
  "close_code",
  "close_notes",
  "correlation_id",
  "correlation_display",
  "work_notes",
]);

const validateCustomFields = (value: string | undefined, context: Yup.TestContext) => {
  if (!value?.trim()) {
    return true;
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(value);
  } catch {
    return context.createError({message: "Invalid JSON provided."});
  }

  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    return context.createError({message: "Root object must be a JSON dictionary."});
  }

  for (const [key, fieldValue] of Object.entries(parsed)) {
    if (!FIELD_NAME_RE.test(key)) {
      return context.createError({
        message: `Invalid field name "${key}". Field names must start with a lowercase letter and contain only lowercase letters, digits and underscores.`,
      });
    }
    if (RESERVED_FIELD_NAMES.has(key)) {
      return context.createError({
        message: `Field "${key}" is managed by this integration and cannot be set here.`,
      });
    }
    if (!["string", "number", "boolean"].includes(typeof fieldValue)) {
      return context.createError({
        message: `Invalid value for field "${key}". Values must be text, numbers or booleans.`,
      });
    }
    if (typeof fieldValue === "number" && !Number.isFinite(fieldValue)) {
      return context.createError({
        message: `Invalid value for field "${key}". Numbers must be finite.`,
      });
    }
  }

  return true;
};

export const ServiceNowShape = {
  instance_url: Yup.string().label("Instance URL").url().min(12).max(255).required(),
  username: Yup.string().label("Username").required(),
  password: Yup.string().label("Password").required(),
  urgency: Yup.string().label("Urgency").optional(),
  impact: Yup.string().label("Impact").optional(),
  assignment_group: Yup.string().label("Assignment Group").max(255).optional(),
  close_code: Yup.string()
    .label("Resolution Code")
    .max(255)
    .when("autoresolve", {
      is: true,
      then: (schema) => schema.required("Resolution Code is required when auto-resolve is enabled"),
      otherwise: (schema) => schema.optional(),
    }),
  autoresolve: Yup.boolean().label("Auto-resolve Incident").optional(),
  include_payload: Yup.boolean().label("Include Full Alert Payload").optional(),
  custom_fields_json: Yup.string()
    .label("Additional Fields")
    .optional()
    .test("valid-custom-fields", (value, context) => validateCustomFields(value, context)),
};

export interface ServiceNowInput {
  instance_url: string;
  username: string;
  password: string;
  urgency?: string;
  impact?: string;
  assignment_group?: string;
  close_code?: string;
  autoresolve?: boolean;
  include_payload?: boolean;
  custom_fields_json?: string;
}

export const ServiceNowDefaults = {
  instance_url: "https://",
  urgency: "",
  impact: "",
  close_code: "Resolved by caller",
  autoresolve: true,
  include_payload: true,
  custom_fields_json: "",
};

yupResolver<ServiceNowInput>(Yup.object().shape(ServiceNowShape));
